import {
  ApiGeneralDataResponse,
  ApiResponse,
  ConfigurationItem,
  MarketplaceSubGroup,
  PlatformType,
  SubGroupType,
  UserImpersonationPermissionLevel,
} from "@shared/types";

export enum ClientStatus {
  ACTIVE = 1,
  INVALID = 2,
  DEACTIVATED_BY_USER = 3,
  DEACTIVATED_BY_SYSTEM = 4,
  DELETED = 5,
  PENDING_INVALID = 6, // DEPRECATED use client.status_pending_invalid
  DELETED_BY_SUB_USER = 7,
  DELETED_BY_ASSET_MANAGER = 8,
}

export enum ExchangeAccountType {
  NORMAL = 1,
  UNIFIED_ACCOUNT = 2,
  UNIFIED_MARGIN = 3,
  MARGIN_FREE = 4,
  MULTI_CURRENCY_MARGIN = 5,
  PORTFOLIO_MARGIN = 6,
  MICA_RESTRICTED = 7,
  MICA_CREDIT_MODE = 8,
  UNIFIED_ACCOUNT_WITH_INVERSE = 9,
  MICA_PARTIALLY_RESTRICTED = 10,
}

export interface ClientData {
  exchange: string;
  client_name: string;
  api_key: string;
  client_uuid: string;
  creation_date: number;
  is_valid: boolean;
  client_id: number;
  has_assigned_ips?: boolean;
  api_expiration_time: number | null;
  client_unique_identifier: string;
  client_status: ClientStatus;
  exchange_account_type?: ExchangeAccountType;
  permission_level?: UserImpersonationPermissionLevel;
  sub_user_invite_prefix?: string;
  sub_user_uid?: number;
  sub_user_permission_level?: UserImpersonationPermissionLevel;
  is_hedge_mode?: boolean;
  demo_maker_fee?: number | null;
  demo_taker_fee?: number | null;
}

export type ClientDataWithId = ClientData & { id: number };

export interface GroupData {
  supported_exchanges?: string[] | null;
  expiration_timestamp?: number;
  recurring_price?: number;
  next_auto_renewal?: number;
  entitled_to_free_trial?: boolean;
  support_payments?: boolean;
  id: number;
  image: string;
  name: string;
  allow_follow?: boolean;
  allow_auto_trading?: boolean;
  is_active?: boolean;
  exchange_to_main_coin_to_last_month_signals_count?: {
    [exchangeName: string]: { [coinName: string]: number };
  };
  avg_stars?: number;
  num_of_reviewers?: number;
  is_free?: boolean;
  has_free_trial?: boolean;
  has_active_subscription?: boolean;
  free_trial_ended?: boolean;
  extra_info?: MarketplaceSubGroup;
  platform_type?: PlatformType;
  is_personal?: boolean;
  sub_group_type?: SubGroupType;
  keyword_to_symbols?: Record<string, string[]>;
}

export type AddClientRequest = {
  exchange?: string;
  api_key?: string;
  api_secret?: string;
  api_passphrase?: string;
  is_demo_query?: boolean;
  demo_initial_funds?: number;
  demo_maker_fee?: number | null;
  demo_taker_fee?: number | null;
  exchange_to_client_name?: Record<string, string>;
};
export type AddClientResponse = ApiResponse<
  ApiGeneralDataResponse<ClientData[]>
>;
export type EditClientRequest = Pick<
  ClientData,
  "client_name" | "client_id"
> & {
  api_key?: string;
  api_secret?: string;
  demo_maker_fee?: number | null;
  demo_taker_fee?: number | null;
};

export interface ConfigurationExchangeInfo {
  exchange: string;
  exchange_display: string;
  is_free: boolean;
  api_passphrase?: boolean;
  info_link?: string;
  should_display: boolean;
  sign_up_link?: string;
  description?: string;
  display_suffix?: string;
  nested_exchange?: string;
  tag?: string;
  is_shared_wallet?: boolean;
}

interface BaseClientsRequest {
  client_ids: number[];
}

export interface ClientsAndGroupRequest extends BaseClientsRequest {
  sub_group_id: number;
}

export interface ClientChannelConfiguration {
  id: number;
  name: string;
}

export interface GetClientChannelConfigurationRequest
  extends BaseClientsRequest {
  trading_configuration_id: number;
}

export interface SetTradingConfigurationRequest extends BaseClientsRequest {
  configuration: ConfigurationItem;
  include_dca_strategies?: boolean;
  is_onboarding?: boolean;
}

export interface GetTradingConfigurationRequest {
  client_id: number;
  include_dca_strategies?: boolean;
}

export interface AccountConfiguration {
  is_hedge_mode?: boolean;
}

export interface GetAccountConfigurationRequest {
  account_id: number;
}

export interface SetAccountConfigurationRequest {
  account_id: number;
  is_hedge_mode: boolean;
}

export type GetAuthUrlRequest = {
  nested_exchange: string;
  exchange_to_client_name: Record<string, string>;
  client_base_name: string;
  authToken?: string;
  user_auth_token?: string;
};

export type CreateAccessTokenRequest = {
  code: string;
  state: string;
  authToken?: string;
};

export type CreateAccessTokenResponse = {
  nested_exchange: string;
  exchange_to_client_name: Record<string, string>;
  client_base_name: string;
};

export type CheckAccountStatusRequest = {
  state: string;
  authToken?: string;
};

export type CheckBinanceAccountStatus = {
  Binance: boolean;
  "Binance Futures": boolean;
  "Binance Coin-Futures": boolean;
  "Binance Cross-Margin": boolean;
};

export type CreateApiKeysRequest = {
  exchange_to_client_name: Record<string, string>;
  state: string;
  authToken?: string;
};

export type GetMemberWhitelistIpsRequest = {
  sub_group_id: number;
  user_id: number;
};

export enum BuyOrSell {
  TAKE_PROFIT = 1,
  ENTRY = 2,
}

export interface BaseCreateCustomStrategyRequest {
  name: string;
  buy_or_sell: BuyOrSell;
  ratios: number[];
}

export interface CreateCustomStrategyRequest
  extends BaseCreateCustomStrategyRequest {
  client_id: number;
}

export interface CreateSubGroupCustomStrategyRequest
  extends BaseCreateCustomStrategyRequest {
  sub_group_id: number;
}

export type DeleteCustomStrategyRequest = {
  type: number;
  client_id: number;
};

export type EditCustomStrategyRequest = Pick<
  CreateCustomStrategyRequest,
  "client_id" | "name" | "ratios"
> & {
  type: number;
};

export type GetWhitelistIpsRequest = {
  client_id?: number;
  authToken?: string;
  user_auth_token?: string;
};

export interface CreateAccountFromInviteRequest extends AddClientRequest {
  authToken: string;
  exchanges?: string[];
  user_auth_token?: string;
}

export enum InvitationStatus {
  PENDING = 1,
  USED = 2,
  EXPIRED = 3,
}

export enum ExternalInvitationType {
  EXTERNAL_INVITE = 1,
  PERSONAL = 2,
}

export interface ExternalAccountInvitation {
  name: string;
  token: string;
  valid_to: number;
  creation_datetime: number;
  status: InvitationStatus;
  type: ExternalInvitationType;
  permission_level: UserImpersonationPermissionLevel;
  asset_manager_permission_level: UserImpersonationPermissionLevel;
}

export interface ExternalAccountInvitationWithKey
  extends ExternalAccountInvitation {
  key: string;
}

export interface ExternalInvitationUserData {
  configuration?: {
    whitelabel?: boolean;
    favicon?: string;
    light_mode_logo?: string;
    dark_mode_logo?: string;
    tab_title?: string;
    supported_exchanges?: string[];
  };
  inviter_light_mode_image?: string;
  inviter_dark_mode_image?: string;
  inviter_name?: string;
  inviter_id?: number;
  account_prefix?: string;
  permission_level: UserImpersonationPermissionLevel;
  asset_manager_permission_level: UserImpersonationPermissionLevel;
  existing_client_names?: string[];
  type: ExternalInvitationType;
}

export enum ExperienceConnectionStatus {
  CONNECTED = 1,
  DISCONNECTED = 2,
}

export enum ExperienceConnectionRequestStatus {
  PENDING = 1,
  USED = 2,
  EXPIRED = 3,
}

export interface CheckExperienceConnectionRequestResponse {
  status: ExperienceConnectionRequestStatus;
}
